import { useMemo } from 'react';
import {
  AddressElement,
  CardNumberElement as CardNumberStripeElement,
  useElements,
  useStripe,
} from '@stripe/react-stripe-js';
import type {
  StripeAddressElementChangeEvent,
  StripeError,
} from '@stripe/stripe-js';
import type { PaymentCard } from '@frameio/graphql-client/generated';
import { reportError } from '@frameio/telemetry';

type StripeData = {
  addressElementValue?: StripeAddressElementChangeEvent['value'];
  stripeError?: StripeError;
  stripeTokenId?: string;
};

type StripeDataFromElements = [
  getData: () => Promise<StripeData | undefined>,
  sources: {
    stripe: ReturnType<typeof useStripe>;
    elements: ReturnType<typeof useElements>;
  },
];

/**
 * Gets the values from the Stripe Elements on the page
 * @param paymentCard - the payment card that is already set
 * @param includeCard - boolean indicating if the card should be included in the update
 * @returns - a tuple containing the getData function and the stripe and elements sources
 */
export function useGetStripeDataFromElements(
  paymentCard?: PaymentCard,
  includeCard = false
): StripeDataFromElements {
  const stripe = useStripe();
  const elements = useElements();

  return useMemo(
    () => [
      async (): Promise<StripeData> => {
        if (!stripe || !elements) {
          // Stripe.js has not loaded yet. Make sure to disable
          // form submission until Stripe.js has loaded.
          return {};
        }

        const cardElement = elements.getElement(CardNumberStripeElement);
        const addressElement = elements.getElement(AddressElement);

        // calling this will trigger form validation on AddressElement
        const addressElementValue: StripeAddressElementChangeEvent['value'] =
          addressElement
            ? (await addressElement.getValue()).value
            : {
                address: {
                  city: paymentCard?.billingAddress?.city ?? '',
                  country: paymentCard?.billingAddress?.country ?? '',
                  line1: paymentCard?.billingAddress?.line1 ?? '',
                  line2: paymentCard?.billingAddress?.line2 ?? null,
                  postal_code: paymentCard?.billingAddress?.postalCode ?? '',
                  state: paymentCard?.billingAddress?.state ?? '',
                },
                name: paymentCard?.card?.name ?? '',
              };

        let stripeTokenId: string | undefined;
        let stripeError: StripeError | undefined;

        if (includeCard && cardElement) {
          // if card form is not active, we will only update the address
          try {
            const result = await stripe.createToken(cardElement, {
              address_city: addressElementValue?.address.city,
              address_country: addressElementValue?.address.country,
              address_line1: addressElementValue?.address.line1,
              address_line2: addressElementValue?.address.line2 ?? undefined,
              address_state: addressElementValue?.address.state,
              address_zip: addressElementValue?.address.postal_code,
              name: addressElementValue?.name,
            });

            stripeTokenId = result.token?.id;
            stripeError = result.error;
          } catch (errorCreatingStripeToken) {
            reportError(errorCreatingStripeToken, {
              message: 'Failed to create Stripe token',
            });
          }
        }

        return {
          addressElementValue,
          stripeError,
          stripeTokenId,
        };
      },
      {
        stripe,
        elements,
      },
    ],
    [elements, includeCard, paymentCard, stripe]
  );
}
